import { V2PvPerformanceAnalyseInverterExpressionPostResponse } from '@maxtropy/dmes-apis-v2';
import { allNull, keepTwoNull, keepTwoOrLine } from '../../utils';
import dayjs from 'dayjs';
import { ResolutionEnum } from '../..';
import circleImg from '../../imgs/circle@2x.png';
import circleBgImg from '../../imgs/circlebg-big@2x.png';
import { isNil } from 'lodash-es';

// 图例
export const formatLegend = (
  inverterChartDataList?: V2PvPerformanceAnalyseInverterExpressionPostResponse['inverterChartDataList']
) => {
  return {
    right: 20,
    itemWidth: 12,
    itemHeight: 12,
    data: inverterChartDataList?.map(item => ({
      name: item.inverterName,
      icon: 'rect',
    })),
  };
};

// 展示不同series
export const formatSeries = (
  chartData?: V2PvPerformanceAnalyseInverterExpressionPostResponse['inverterChartDataList']
) => {
  return [
    ...(chartData ?? []).map(item => ({
      name: item.inverterName,
      type: 'bar',
      yAxisIndex: 0,
      data: item.inverterChartDataList?.map(c => [dayjs(c.time).valueOf(), keepTwoNull(c.power), item.inverterName]),
    })),
    ...(chartData ?? []).map(item => ({
      name: item.inverterName,
      type: 'line',
      data: item.inverterChartDataList?.map(c => [
        dayjs(c.time).valueOf(),
        keepTwoNull(c.availableHour),
        item.inverterName,
      ]),
      yAxisIndex: 1,
      connectNulls: true,
      symbol: 'circle',
      symbolSize: 2,
      showSymbol: false, // 只有在 tooltip hover 的时候显示symbol
      itemStyle: {
        // symbol样式
        normal: {
          borderColor: 'rgba(255, 255, 255, 0.2)', // symbol边框颜色
          borderWidth: 6, // symbol边框宽度
        },
      },
    })),
  ];
};

// 展示间隔
export const calcIntervalValue = (
  chartData?: V2PvPerformanceAnalyseInverterExpressionPostResponse['inverterChartDataList']
) => {
  // 逆变器发电量
  const inverterPowerData = chartData
    ?.map(item => {
      return item.inverterChartDataList?.map(item => [dayjs(item.time).valueOf(), keepTwoNull(item.power)]);
    })
    .flat();
  const maxInverterPowerData = Math.max(...(inverterPowerData ?? []).map(i => i?.[1] ?? 0)); // 逆变器发电量
  const minInverterPowerData = Math.min(...(inverterPowerData ?? []).map(i => i?.[1] ?? 0)); // 逆变器发电量

  const max = Math.ceil(Math.max(maxInverterPowerData));
  const min = Math.floor(Math.min(minInverterPowerData));
  // 间隔计算
  const interVal = Number(((max - min) / 4).toFixed(0));
  return { max, min, interVal };
};

export const getChartOption = (
  chartData?: V2PvPerformanceAnalyseInverterExpressionPostResponse,
  resolution?: ResolutionEnum
) => {
  if (chartData?.inverterChartDataList) {
    const formatDate = resolution === ResolutionEnum.YEAR ? 'YYYY-MM' : 'MM-DD';
    const dataLength = chartData?.inverterChartDataList?.length ?? 0;
    const sliceIndex = dataLength > 10 ? 10 : dataLength;
    const formatShowChartData = chartData?.inverterChartDataList?.slice(0, sliceIndex); // 取前10个展示，剩余用剩余展示
    const restChartData = chartData?.inverterChartDataList?.slice(sliceIndex, chartData?.inverterChartDataList?.length);
    const finalChartData =
      chartData?.inverterChartDataList.length > 10
        ? [
            ...(formatShowChartData ?? []),
            {
              inverterName: '其他',
              inverterChartDataList: (restChartData?.[0].inverterChartDataList ?? []).map(item => ({
                time: item.time,
                power: allNull(restChartData?.map(i => i.inverterChartDataList?.find(x => x.time === item.time)?.power))
                  ? undefined
                  : restChartData
                      .map(c => c.inverterChartDataList?.find(x => x.time === item.time)?.power)
                      ?.reduce((pre, cur) => (cur || 0) + (pre || 0), 0),
                availableHour: allNull(
                  restChartData?.map(i => i.inverterChartDataList?.find(x => x.time === item.time)?.availableHour)
                )
                  ? undefined
                  : restChartData
                      .map(c => c.inverterChartDataList?.find(x => x.time === item.time)?.availableHour)
                      ?.reduce((pre, cur) => (cur || 0) + (pre || 0), 0),
              })),
            },
          ]
        : [...(formatShowChartData ?? [])];

    const legend = formatLegend(finalChartData);

    // 可用小时数间隔计算
    let availableHourData = chartData?.inverterChartDataList
      ?.map(item => {
        return item.inverterChartDataList?.map(item => [dayjs(item.time).valueOf(), keepTwoNull(item.availableHour)]);
      })
      .flat();
    const maxAvailableHourData = Math.max(...(availableHourData ?? []).map(i => i?.[1] ?? 0)); // 可用小时数
    const minAvailableHourData = Math.min(...(availableHourData ?? []).map(i => i?.[1] ?? 0)); // 可用小时数

    const maxAvailableHour = Math.ceil(Math.max(maxAvailableHourData));
    const minAvailableHour = Math.floor(Math.min(minAvailableHourData));
    // 可用小时数间隔计算
    const availableHourInterVal = Number(((maxAvailableHour - minAvailableHour) / 4).toFixed(0));

    const series = formatSeries(finalChartData);

    // 左侧Y轴计算
    const { max, min, interVal } = calcIntervalValue(finalChartData);

    return {
      tooltip: {
        formatter: (items: any) => {
          let { axisValue } = items[0];
          let firstStr = dayjs(axisValue).format(formatDate);
          let otherStr = '';
          let str = '';
          items
            .filter((x: { seriesType: string }) => x?.seriesType === 'bar')
            .forEach((item: any) => {
              const { seriesName, data } = item;
              const availableTime = items
                .filter((x: any) => x?.seriesType === 'line')
                .find((c: any) => c?.data?.[2] === item?.data[2])?.data?.[1];
              const renderTime = !isNil(availableTime) ? availableTime + 'h' : '--';
              let unit = 'kWh';
              otherStr += `<div style="margin-top:8px"><span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${
                item.color
              };"></span>${seriesName} : &nbsp;${!isNil(data[1]) ? data[1] + unit : '--'} &nbsp;${renderTime}</div>`;
              str = firstStr + otherStr;
            });
          return str;
        },
      },
      grid: {
        left: 20,
        right: 50,
        top: 60,
        bottom: 40,
        containLabel: true,
      },
      dataZoom: {
        type: 'slider',
        left: 20,
        right: 20,
        height: 20,
        bottom: 10,
        textStyle: {
          fontSize: 10,
        },
      },
      legend: legend,
      xAxis: [
        {
          type: 'time',
          axisLabel: {
            formatter: function (e: number) {
              return dayjs(e, 'x').format(formatDate);
            },
          },
          axisPointer: {
            type: 'shadow',
          },
        },
      ],
      yAxis: [
        {
          type: 'value',
          name: '发电量（kWh）',
          // splitNumber: 5, //优先级太低只能硬算
          max: max,
          min: min,
          interval: interVal,
          axisLabel: {
            formatter: '{value}',
          },
        },
        {
          type: 'value',
          name: '可用小时数（h）',
          max: maxAvailableHour,
          min: minAvailableHour,
          interval: availableHourInterVal,
          axisLabel: {
            formatter: '{value}',
          },
        },
      ],
      series: series,
    };
  }
};

export const getPieChartOption = (
  data?: V2PvPerformanceAnalyseInverterExpressionPostResponse['inverterDataList'],
  middleScreen?: boolean
) => {
  const totalPower = data?.reduce((pre, cur) => (cur.power || 0) + pre, 0);
  const dataLength = data?.length ?? 0;
  const sliceIndex = dataLength > 10 ? 10 : dataLength;
  // 根据power值进行排序,降序
  const chartData = data?.sort((a, b) => (b.power ?? 0) - (a.power ?? 0));
  const formatShowChartData = chartData?.slice(0, sliceIndex); // 取前10个展示，剩余用剩余展示
  const restChartData = (chartData ?? []).slice(sliceIndex, (chartData ?? []).length);
  const restTotalPower = restChartData?.reduce((pre, cur) => (cur.power || 0) + pre, 0);
  const finalChartData =
    (chartData ?? []).length > sliceIndex
      ? [
          ...(formatShowChartData ?? []),
          {
            inverterName: '其他',
            power: allNull(restChartData?.map(i => i.power)) ? undefined : restTotalPower,
          },
        ]
      : [...(formatShowChartData ?? [])];
  return {
    // color: ['#FFCB47', '#16DD8E', '#2D8DFF', '#556583'],
    legend: {
      left: '40%',
      top: 'center',
      orient: 'vertical',
      icon: 'circle',
      itemGap: 8,
      itemHeight: 7,
      itemWidth: 7,
      textStyle: {
        rich: {
          name: {
            fontSize: 14,
            lineHeight: 20,
            width: 170,
          },
          percent: {
            fontSize: 14,
            width: 100,
          },
          value: {
            fontSize: 14,
            width: 120,
          },
        },
      },
      formatter: (name: string) => {
        const item = finalChartData?.find(item => item.inverterName === name);
        let inverterName =
          (item?.inverterName ?? '').length > 15 ? item?.inverterName?.slice(0, 15) + '...' : item?.inverterName;
        const percent = totalPower ? (((item?.power ?? 0) / totalPower) * 100).toFixed(2) + '%' : '-';
        return `{name| ${inverterName}}{percent| ${percent ?? '-'}}{value|${keepTwoOrLine(item?.power) + 'kWh'}}`;
      },
    },
    title: {
      text: allNull(chartData?.map(i => i.power))
        ? '-'
        : (chartData ?? []).length
        ? chartData?.reduce((pre, cur) => (cur.power || 0) + pre, 0).toFixed(2)
        : '-',
      subtext: '发电量(kWh)',
      left: middleScreen ? 108 : 208,
      top: 98,
      itemGap: 8,
      textAlign: 'center',
    },
    graphic: {
      elements: [
        {
          type: 'image',
          style: {
            image: circleImg,
            x: middleScreen ? 56 : 156,
            y: 66,
            width: 112,
            height: 112,
          },
        },
        {
          type: 'image',
          style: {
            image: circleBgImg,
            x: middleScreen ? 36 : 136,
            y: 46,
            width: 152,
            height: 152,
            opacity: allNull(chartData?.map(i => i.power)) ? 1 : 0,
          },
        },
      ],
    },
    series: [
      {
        type: 'pie',
        name: '发电量(kWh)',
        percentPrecision: 5,
        minAngle: 2,
        padAngle: 1,
        radius: [58, 72],
        left: middleScreen ? 50 : 150,
        top: 60,
        width: 124,
        height: 124,
        label: {
          show: false,
        },
        data: (finalChartData ?? []).map(item => ({
          name: item.inverterName,
          value: keepTwoOrLine(item.power),
        })),
        showEmptyCircle: false,
      },
      {
        showEmptyCircle: false,
        // emptyCircleStyle: {
        //   color: 'rgba(82,231,255,0.05)',
        // },
        data: (finalChartData ?? [])?.map(item => ({
          name: item.inverterName,
          value: keepTwoOrLine(item.power),
        })),
        type: 'pie',
        percentPrecision: 5,
        padAngle: 1,
        minAngle: 2,
        left: middleScreen ? 50 : 150,
        top: 60,
        name: '发电量(kWh)',
        radius: [66, 86],
        width: 124,
        height: 124,
        label: {
          show: false,
        },
        itemStyle: {
          opacity: 0.2,
        },
      },
    ],
  };
};
